/**
 * FilePreviewModal Component - Full-screen modal for previewing images and PDFs
 * Supports navigation between multiple files with keyboard navigation
 */

import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { Attachment } from '@/data/attachmentTypes';
import { isImageMimeType } from '@/data/attachmentTypes';
import { getDownloadUrl } from '@/services/attachment.service';

interface FilePreviewModalProps {
  attachments: Attachment[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
}

export function FilePreviewModal({
  attachments,
  initialIndex = 0,
  open,
  onOpenChange,
  className,
}: FilePreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});

  // Reset index when modal opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setLoaded(false);
      setResolvedUrl(null);
      setUrlError(false);
    }
  }, [open, initialIndex]);

  // Resolve the current attachment's signed URL whenever the attachment or modal state changes
  useEffect(() => {
    const att = attachments[currentIndex];
    if (!att || !open) return;

    let cancelled = false;
    setLoaded(false);
    setResolvedUrl(null);
    setUrlError(false);

    getDownloadUrl(att.id, 3600)
      .then((url) => {
        if (!cancelled) setResolvedUrl(url);
      })
      .catch((err) => {
        if (!cancelled) {
          setUrlError(true);
          console.error("FilePreviewModal: failed to resolve preview URL", err);
        }
      });

    return () => { cancelled = true; };
  }, [open, currentIndex, attachments]);

  // Pre-resolve thumbnail URLs for the image strip (images only)
  useEffect(() => {
    if (!open || attachments.length <= 1) {
      setThumbnailUrls({});
      return;
    }

    let cancelled = false;
    const map: Record<string, string> = {};

    Promise.all(
      attachments.map(async (att) => {
        if (!isImageMimeType(att.mimeType)) return;
        try {
          map[att.id] = await getDownloadUrl(att.id, 600);
        } catch { /* leave unresolved — falls back to filePath */ }
      }),
    ).then(() => {
      if (!cancelled) setThumbnailUrls(map);
    });

    return () => { cancelled = true; };
  }, [open, attachments]);

  // Get current attachment
  const current = attachments[currentIndex];
  const isImage = current ? isImageMimeType(current.mimeType) : false;
  const hasMultiple = attachments.length > 1;

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + attachments.length) % attachments.length);
    setLoaded(false);
    setResolvedUrl(null);
    setUrlError(false);
  }, [attachments.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % attachments.length);
    setLoaded(false);
    setResolvedUrl(null);
    setUrlError(false);
  }, [attachments.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          onOpenChange(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, goToPrevious, goToNext, onOpenChange]);

  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn(
          "max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none",
          isFullscreen && "max-w-[100vw] max-h-[100vh] rounded-none",
          className
        )}
      >
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4 bg-gradient-to-b from-black/60 to-transparent">
          <div className="text-white">
            <p className="text-sm font-medium">{current.fileName}</p>
            {hasMultiple && (
              <p className="text-xs text-white/70">
                {currentIndex + 1} of {attachments.length}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Download button */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              asChild
            >
              <a 
                 href={resolvedUrl ?? current.filePath}
                 download={current.fileName}
                 target="_blank"
                 rel="noopener noreferrer"
                 onClick={(e) => e.stopPropagation()}
               >
                <Download className="h-4 w-4" />
              </a>
            </Button>
            
            {/* Fullscreen toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              {isFullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
            
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => onOpenChange(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex items-center justify-center min-h-[60vh]">
          {urlError ? (
            <p className="text-white/50 text-sm">
              Could not load preview. The file may have been moved or is no longer accessible.
            </p>
          ) : !resolvedUrl ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          ) : !loaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}
          
          {resolvedUrl && isImage ? (
            <img
              src={resolvedUrl}
              alt={current.fileName}
              className={cn(
                "max-w-full max-h-[80vh] object-contain transition-opacity",
                loaded ? "opacity-100" : "opacity-0"
              )}
              onLoad={() => setLoaded(true)}
              onError={() => setUrlError(true)}
            />
          ) : resolvedUrl ? (
            <iframe
              src={resolvedUrl}
              className="w-[80vw] h-[70vh] max-w-4xl"
              title={current.fileName}
              onLoad={() => setLoaded(true)}
              onError={() => setUrlError(true)}
            />
          ) : null}
        </div>

        {/* Navigation arrows */}
        {hasMultiple && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12"
              onClick={goToPrevious}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 h-12 w-12"
              onClick={goToNext}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </>
        )}

        {/* Thumbnail strip for multiple images */}
        {hasMultiple && isImage && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 p-2 bg-black/60 rounded-lg">
            {attachments.map((att, idx) => (
              <button
                key={att.id}
                type="button"
                 onClick={() => {
                   setCurrentIndex(idx);
                   setLoaded(false);
                   setResolvedUrl(null);
                   setUrlError(false);
                 }}
                className={cn(
                  "w-12 h-12 rounded overflow-hidden border-2 transition-colors",
                  idx === currentIndex ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                )}
              >
                 <img
                   src={thumbnailUrls[att.id] ?? att.filePath}
                   alt={att.fileName}
                   className="w-full h-full object-cover"
                 />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Simple image preview component for single images
 */
interface ImagePreviewProps {
  src: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
}

export function ImagePreview({ src, alt = '', className, onClick }: ImagePreviewProps) {
  return (
    <button
      type="button"
      className={cn("rounded-lg overflow-hidden bg-muted", className)}
      onClick={onClick}
    >
      <img 
        src={src} 
        alt={alt}
        className="w-full h-full object-cover hover:scale-105 transition-transform"
      />
    </button>
  );
}

export default FilePreviewModal;